import { Crown, Phone, MapPin, Clock, Instagram, Facebook, Headset } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { SITE, scrollToId } from "../data/site";
import { useOrder } from "./OrderDialog";
import { useLang } from "../i18n";

export const Footer = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const order = useOrder();
    const { t } = useLang();

    const goSection = (id) => {
        if (location.pathname !== "/") navigate("/", { state: { scrollTo: id } });
        else scrollToId(id);
    };
    const goMenu = () => {
        navigate("/menu");
        window.__lenis?.scrollTo(0, { immediate: true });
    };

    return (
        <footer data-testid="site-footer" className="relative border-t border-brass/15 bg-ink pb-24 md:pb-0">
            <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-12">
                    <div>
                        <div className="flex items-center gap-3">
                            <span className="w-10 h-10 rounded-full gold-border bg-elevated/70 flex items-center justify-center">
                                <Crown className="w-[18px] h-[18px] text-saffron-bright" strokeWidth={1.5} />
                            </span>
                            <span className="font-cinzel font-bold tracking-[0.18em] text-cream">ROYAL TADKA</span>
                        </div>
                        <p className="mt-5 text-sm text-stone-400 font-light leading-relaxed max-w-xs">
                            {t("footer.tagline")}
                        </p>
                        <div className="mt-6 flex gap-3">
                            <a
                                data-testid="footer-instagram"
                                href="https://www.instagram.com/royaltadka2026"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Royal Tadka on Instagram"
                                className="w-10 h-10 rounded-full gold-border flex items-center justify-center text-stone-300 hover:text-saffron-glow hover:border-saffron/50 transition-[color,border-color] duration-300"
                            >
                                <Instagram className="w-4 h-4" strokeWidth={1.5} />
                            </a>
                            <a
                                data-testid="footer-facebook"
                                href="https://www.facebook.com/share/1BWoP8Mu2u/"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Royal Tadka on Facebook"
                                className="w-10 h-10 rounded-full gold-border flex items-center justify-center text-stone-300 hover:text-saffron-glow hover:border-saffron/50 transition-[color,border-color] duration-300"
                            >
                                <Facebook className="w-4 h-4" strokeWidth={1.5} />
                            </a>
                        </div>
                    </div>

                    <nav aria-label="Explore">
                        <p className="text-xs tracking-[0.3em] uppercase text-brass">{t("footer.explore")}</p>
                        <ul className="mt-5 space-y-3 text-sm">
                            {[
                                { key: "home", fn: () => goSection("home"), testId: "footer-link-home" },
                                { key: "story", fn: () => goSection("story"), testId: "footer-link-story" },
                                { key: "menu", fn: goMenu, testId: "footer-link-menu" },
                                { key: "gallery", fn: () => goSection("gallery"), testId: "footer-link-gallery" },
                                { key: "contact", fn: () => goSection("contact"), testId: "footer-link-contact" },
                            ].map((l) => (
                                <li key={l.key}>
                                    <button
                                        data-testid={l.testId}
                                        onClick={l.fn}
                                        className="text-stone-400 hover:text-saffron-glow transition-colors duration-300"
                                    >
                                        {t(`nav.${l.key}`)}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <nav aria-label="Customer">
                        <p className="text-xs tracking-[0.3em] uppercase text-brass">{t("footer.customer")}</p>
                        <ul className="mt-5 space-y-3 text-sm">
                            <li>
                                <button
                                    data-testid="footer-link-order"
                                    onClick={() => order?.openOrder()}
                                    className="text-stone-400 hover:text-saffron-glow transition-colors duration-300"
                                >
                                    {t("footer.orderOnline")}
                                </button>
                            </li>
                            <li>
                                <button data-testid="footer-link-delivery" onClick={() => goSection("order")} className="text-stone-400 hover:text-saffron-glow transition-colors duration-300">
                                    {t("footer.delivery")}
                                </button>
                            </li>
                            <li>
                                <button data-testid="footer-link-pickup" onClick={() => goSection("order")} className="text-stone-400 hover:text-saffron-glow transition-colors duration-300">
                                    {t("footer.pickup")}
                                </button>
                            </li>
                            <li>
                                <button data-testid="footer-link-loyalty" onClick={() => goSection("loyalty")} className="text-stone-400 hover:text-saffron-glow transition-colors duration-300">
                                    {t("footer.loyalty")}
                                </button>
                            </li>
                        </ul>
                    </nav>

                    <div>
                        <p className="text-xs tracking-[0.3em] uppercase text-brass">{t("footer.contact")}</p>
                        <ul className="mt-5 space-y-4 text-sm text-stone-400">
                            <li className="flex gap-3">
                                <MapPin className="w-4 h-4 text-saffron-bright shrink-0 mt-0.5" strokeWidth={1.5} />
                                <a
                                    data-testid="footer-address-link"
                                    href={SITE.mapsUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hover:text-saffron-glow transition-colors duration-300"
                                >
                                    {SITE.addressLine1}
                                    <br />
                                    {SITE.addressLine2}
                                </a>
                            </li>
                            <li className="flex gap-3">
                                <Phone className="w-4 h-4 text-saffron-bright shrink-0 mt-0.5" strokeWidth={1.5} />
                                <a href={SITE.phoneHref} data-testid="footer-phone" className="hover:text-saffron-glow transition-colors duration-300">
                                    {SITE.phoneDisplay}
                                </a>
                            </li>
                            <li className="flex gap-3">
                                <Clock className="w-4 h-4 text-saffron-bright shrink-0 mt-0.5" strokeWidth={1.5} />
                                <span>{t("footer.hours")}</span>
                            </li>
                        </ul>
                        <div className="mt-6 flex flex-wrap gap-x-4 gap-y-2 text-xs text-stone-500">
                            <a data-testid="footer-privacy" href="#" className="hover:text-stone-300 transition-colors duration-300">{t("footer.privacy")}</a>
                            <a data-testid="footer-terms" href="#" className="hover:text-stone-300 transition-colors duration-300">{t("footer.terms")}</a>
                            <a data-testid="footer-allergens" href="#" className="hover:text-stone-300 transition-colors duration-300">{t("footer.allergens")}</a>
                        </div>
                    </div>
                </div>

                <div
                    data-testid="support-strip"
                    className="mt-14 rounded-2xl border border-brass/10 bg-onyx/50 px-6 py-5 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 text-center sm:text-left"
                >
                    <Headset className="w-5 h-5 text-brass/80 shrink-0" strokeWidth={1.5} />
                    <p className="text-xs sm:text-[13px] text-stone-500 font-light leading-relaxed">
                        {t("support.line1")} {t("support.line2")}{" "}
                        <a
                            data-testid="support-email-link"
                            href="mailto:eshanegi6@gmail.com"
                            className="text-saffron-glow hover:text-cream transition-colors duration-300 underline underline-offset-4 decoration-brass/40"
                        >
                            eshanegi6@gmail.com
                        </a>
                    </p>
                </div>

                <div className="mt-10 pt-8 border-t border-brass/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-stone-500">
                    <p>{t("footer.rights")}</p>
                    <p className="font-serif italic text-stone-500">{t("footer.motto")}</p>
                </div>
            </div>
        </footer>
    );
};
